import type { StringSchemaOptions } from "shelving";
import { Feedback, LinkSchema, NumberSchema, OPTIONAL, StringSchema, countItems, getOptionalRef, getRandomItem, getValidationContext } from "shelving";
import { CONTENT_TYPES, CREATOR_TYPES } from "./entity.js";
import { getTagElements } from "./markup.js";

// Constants.
export const LONGFORM_MAX_TAGS = 20;
export const LONGFORM_MAX_LENGTH = 2000;

/**
 * Names that cannot be used with `NameSchema`
 * - Generally things that might clash with `GroupRouter` or `ProfileRouter` or `CreatorRouter`
 * - Note that `NameSchema` checks the singular (e.g. `challenge`) and plural (e.g. `challenges`) by adding an `s`
 */
export const RESERVED_NAMES = [
	// Entities.
	...CREATOR_TYPES,
	...CONTENT_TYPES,
	// Rewards.
	"badge",
	"background",
	"reaction",
	"frame",
	"bling",
	// Group pages.
	"about",
	"message",
	"setting",
	"invite",
	"assigned",
	"leaderboard",
	"member",
	"assigned",
	// Profile pages.
	"following",
	"follower",
	"followed",
	"earned",
	"favorite",
	"favourite",
	"saved",
];

/**
 * Specify a string name field.
 * - These are sometimes converted to slug so have additional rules.
 * - Applies an additional check that when the string is converted to a ref it doesn't have zero length.
 * - Applies an additional check that the string as a ref is not in the reserved list.
 */
export class NameSchema extends StringSchema {
	constructor(options: Omit<StringSchemaOptions, "min" | "max">) {
		super({ ...options, min: 1, max: 40 });
	}
	override validate(unsafeValue: unknown): string {
		const str = super.validate(unsafeValue);
		const { action } = getValidationContext();
		if (action === "set" || action === "update") {
			const ref = getOptionalRef(str);
			if (!ref) throw new Feedback("Must include A-Z", str);
			if (RESERVED_NAMES.includes(ref) || RESERVED_NAMES.includes(`${ref}s`)) throw new Feedback("Cannot use reserved name", ref);
		}
		return str;
	}
}

/** Ideally `Name Case` name string. */
export const NAME = new NameSchema({ title: "Name" });

/**
 * Specify string field for longform content text that might include `@tags` and `#tags`
 * - Applies an additional check that the field contains no more than `WORDS_MAX` words.
 */
export class LongformSchema extends StringSchema {
	readonly maxTags: number;
	constructor({ maxTags = LONGFORM_MAX_TAGS, max = LONGFORM_MAX_LENGTH, multiline = true, ...rest }) {
		super({ max, multiline, ...rest });
		this.maxTags = maxTags;
	}
	override validate(unsafeValue: unknown): string {
		const safeString = super.validate(unsafeValue);
		const { action } = getValidationContext();
		if (action === "set" || action === "update") {
			if (countItems(getTagElements(safeString)) > this.maxTags) throw new Feedback(`Maximum ${this.maxTags} #tags or @tags`, safeString);
		}

		return safeString;
	}
}

export const CONTENT = new LongformSchema({ title: "Content", min: 1 });
export const ABOUT = new LongformSchema({ title: "About" });

export const HUE = new NumberSchema({ value: 0, min: 0, max: 360, step: 1 });
export const OPTIONAL_HUE = OPTIONAL(HUE);

export const WEBSITE = new LinkSchema({ title: "Website" });
export const OPTIONAL_WEBSITE = OPTIONAL(WEBSITE);

export const LOCATION = new StringSchema({
	title: "Location",
	placeholder: `e.g. ${getRandomItem([
		"Astana, Kazakhstan",
		"Buenos Aires, Argentina",
		"Cairo, Egypt",
		"Christchurch, New Zealand",
		"Fiji",
		"Gdansk, Poland",
		"Helsinki, Finland",
		"Lagos, Nigeria",
		"Lima, Peru",
		"Madagasgar",
		"Manchester, UK",
		"Marakesh, Morocco",
		"Mumbai, India",
		"Naples, Italy",
		"Perth, Australia",
		"Pretoria, South Africa",
		"São Paulo, Brazil",
		"Singapore",
		"St Louis, USA",
		"St Petersberg, Russia",
		"Tijuana, Mexico",
		"Valencia, Spain",
		"Vancouver, Canada",
		"Vilnius, Lithuania",
	])}`,
});
export const OPTIONAL_LOCATION = OPTIONAL(LOCATION);
